// @ts-nocheck
import { all, call, put, takeEvery } from "redux-saga/effects";
import { SagaIterator } from "redux-saga";
import { ForgotPasswordI, LoginI, authTypes } from "./authTypes";
import { forgotPasswordFailure, forgotPasswordSuccess, getHelpInfoFailure, getHelpInfoSuccess, getRefreshTokenFailure, getRefreshTokenSuccess, getUsersFailure, getUsersSuccess, loginFailure, loginSuccess } from "./authActions";
import AuthService from "./service";
import { notify } from "../notification/notificationActions";
import { clearStorage, getSecureStorage, saveSecureStorage } from "../../utils/storage";
import { formatSaveStorage } from "../../utils/parseData";

export function* login({ payload }: LoginI): SagaIterator {
  try {
    const { data } = yield call<typeof AuthService.login>(AuthService.login, payload);
    const user = formatSaveStorage(data);

    yield put(loginSuccess(user));
    saveSecureStorage(user, "user");
  } catch (error) {
    yield put(loginFailure(error?.message));
    yield put(notify({ message: error?.message, type: "error" }));
  }
}

export function* logout() {
  clearStorage();
}

export function* forgotPassword({ payload, onSuccess }: ForgotPasswordI): SagaIterator {
  try {
    yield call<typeof AuthService.forgotPassword>(AuthService.forgotPassword, payload);
    yield put(forgotPasswordSuccess());
    if (onSuccess) yield call(onSuccess);
    yield put(notify({ message: "We've just sent you an email to reset your password.", type: "success" }));
  } catch (error: any) {
    yield put(forgotPasswordFailure(error?.message || ""));
    yield put(notify({ message: "Something went wrong.\nPlease contact support", type: "error" }));
  }
}

export function* getHelpInfo(): SagaIterator {
  try {
    const { data } = yield call<typeof AuthService.getHelpInfo>(AuthService.getHelpInfo);

    yield put(getHelpInfoSuccess(data[0].data));
  } catch (error) {
    yield put(getHelpInfoFailure(error?.message));
  }
}

export function* getUsers(): SagaIterator {
  try {
    const { data } = yield call<typeof AuthService.getUsers>(AuthService.getUsers);

    yield put(getUsersSuccess(data[0]));
  } catch (error) {
    yield put(getUsersFailure(error?.message));
  }
}

export function* getRefreshToken({ payload, actionToDispatch }: any): SagaIterator {
  try {
    const hasRefreshTokenBeUsed = yield call(getSecureStorage, "refresh");

    if (!hasRefreshTokenBeUsed) {
      const user = yield call(getSecureStorage, "user");

      yield call(saveSecureStorage, true, "refresh");
      const { data } = yield call<typeof AuthService.getNewAccessToken>(AuthService.getNewAccessToken, {
        refresh_token: user.refresh_token,
      });
      const token = data.access_token;
      const expires_in = Date.now() + data.expires_in * 1000;
      const { refresh_token } = data;
      const refresh_expires_in = Date.now() + (data.refresh_expires_in ? data.refresh_expires_in : 604800) * 1000;
      const newUser = { ...user, access_token: token, refresh_token, refresh_expires_in, expires_in };

      yield call(saveSecureStorage, newUser, "user");
      yield put(getRefreshTokenSuccess(newUser));
    }
    yield put(actionToDispatch(payload));
  } catch (error) {
    yield put(getRefreshTokenFailure(error.message));
  }
}

export function* onLogin() {
  yield takeEvery(authTypes.LOGIN, login);
}

export function* onLogout() {
  yield takeEvery(authTypes.LOGOUT, logout);
}

export function* onForgotPassword() {
  yield takeEvery(authTypes.FORGOT_PASSWORD, forgotPassword);
}

export function* onGetHelpInfo() {
  yield takeEvery(authTypes.GET_HELP_INFO, getHelpInfo);
}

export function* onGetUsers() {
  yield takeEvery(authTypes.GET_USERS, getUsers);
}

export function* onGetRefreshToken() {
  yield takeEvery(authTypes.GET_REFRESH_TOKEN, getRefreshToken);
}

export default function* authSaga() {
  yield all([
    call(onLogin),
    call(onLogout),
    call(onForgotPassword),
    call(onGetHelpInfo),
    call(onGetUsers),
    call(onGetRefreshToken),
  ]);
}
